/* eslint-disable no-param-reassign */
import { isEmpty, sortBy } from 'lodash';
import * as FOC from 'common/constants/fund-ownership/fundOwnership';
import { parseDatabaseValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { FIXED_DECIMAL_PLACES } from '../constants';
import { FundOwnershipCell, ReverseParsedFundOwnershipDetail } from '../types';
import getTotalValue from './getTotalValue';
import rowConfig from './rowConfig';

const reverseParser = ({ cells, columns, fieldAttributes = new Map(), tableData }: {
  cells: Record<string, FundOwnershipCell>;
  columns: ReverseParsedFundOwnershipDetail[];
  fieldAttributes: Map<string, any>;
  tableData: any;
}) => {
  const tmpColumns = columns ?? tableData.columns;
  const sortedColumns = sortBy(tmpColumns, ['order']);
  const rows = rowConfig();
  const tableColumns: ReverseParsedFundOwnershipDetail[] = [];
  const alphabet = alphabetGenerator([], tmpColumns.length);
  const fodAttributes = fieldAttributes.get('fundownershipdetail') ?? {};
  const foAttributes = fieldAttributes.get('fundownership') ?? {};

  sortedColumns.forEach((column, columnIndex) => {
    const columnLegend = alphabet[columnIndex];
    const cell = cells[columnLegend + 1];
    const isCustomSecurityColumn = column.is_custom_security;

    let tmpColumn = column;
    let rowIndex = 1;

    tmpColumn.columnRef = cell ? cell.columnRef! : '';
    tmpColumn.order = columnIndex;

    // Replace updated values
    rows.forEach(row => {
      const { alias, allowEmptyValues, gridType } = row;
      const key = columnLegend + rowIndex;
      const defaultValue = row.defaultValue || null;
      const type = row.dbType ?? null;
      const format = row.format || null;
      const value = cells[key] ? cells[key].value : defaultValue;

      let parsedValue = parseDatabaseValue({
        type,
        value,
        format,
        allowEmptyValues: allowEmptyValues ?? false,
        gridType,
        decimalPlaces: fodAttributes[row.alias]?.decimal_places ?? FIXED_DECIMAL_PLACES,
      });
      if (alias === 'security' && isCustomSecurityColumn) {
        parsedValue = null;
      }

      const { invested_capital, shares } = tmpColumn;

      if (alias && cells[key]) {
        tmpColumn = {
          ...tmpColumn,

          // during js->ts migration many types were completely screwed up, there are some places where we need to cast to unknown
          invested_capital: Number(invested_capital) as unknown as string,
          shares: Number(shares) as unknown as string,
          [alias]: parsedValue,
        };
      }
      if (cell?.acquisitions) {
        tmpColumn.acquisitions = cell.acquisitions;
      }
      if (cell?.deleted_acquisitions) {
        tmpColumn.deleted_acquisitions = cell.deleted_acquisitions;
      }

      rowIndex += 1;
    });
    
    tmpColumn.fund_entity = Number(tmpColumn.fund_entity);
    tmpColumn.fund_id = tmpColumn.fund_entity;
    tmpColumn.fund = tmpColumn.fund_entity;
    tableColumns.push(tmpColumn);
  });

  if (!isEmpty(tableData.fundOwnership)) {
    tableData.fundOwnership.fund_ownership_detail = tableColumns;
    tableData.fundOwnership.invested_capital = getTotalValue(
      FOC.INVESTMENT_CAPITAL_ROW_NUMBER,
      FOC.INVESTMENT_CAPITAL_ALIAS,
      cells,
      foAttributes
    );
    tableData.fundOwnership.shares = getTotalValue(FOC.SHARES_ROW_NUMBER, FOC.SHARES_ALIAS, cells, foAttributes);
    tableData.fundOwnership.shares_converted = getTotalValue(
      FOC.SHARES_AS_CONVERTED_ROW_NUMBER,
      FOC.SHARES_AS_CONVERTED_ALIAS,
      cells,
      foAttributes
    );
    tableData.fundOwnership.diluted_own_percent = getTotalValue(
      FOC.FULLY_DILUTED_OWNERSHIP_ROW_NUMBER,
      FOC.FULLY_DILUTED_OWNERSHIP_ALIAS,
      cells,
      foAttributes,
      100
    );
    tableData.fundOwnership.loan_value = getTotalValue(
      FOC.LOAN_VALUE_ROW_NUMBER,
      FOC.LOAN_VALUE_ALIAS,
      cells,
      foAttributes
    );
    tableData.fundOwnership.initial_liquidation_pref = getTotalValue(
      FOC.INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER,
      FOC.INITIAL_LIQUIDATION_PREFERENCE_ALIAS,
      cells,
      foAttributes
    );
    tableData.fundOwnership.liquidation_pref_dividends = getTotalValue(
      FOC.LIQUIDATION_PREFERENCE_PLUS_ROW_NUMBER,
      FOC.LIQUIDATION_PREFERENCE_PLUS_ALIAS,
      cells,
      foAttributes
    );
    tableData.fundOwnership.cash_distribution = getTotalValue(
      FOC.CASH_DISTRIBUTION_ROW_NUMBER,
      FOC.CASH_DISTRIBUTION_ALIAS,
      cells,
      foAttributes
    );
    tableData.fundOwnership.shares_distribution = getTotalValue(
      FOC.PROCEEDS_FROM_SOLD_SHARES_ROW_NUMBER,
      FOC.PROCEEDS_FROM_SOLD_SHARES_ALIAS,
      cells,
      foAttributes
    );
  }
  return tableData;
};

export default reverseParser;
